import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle2, FileText, Eye, Download, Mail, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DocumentUploadButton } from '@/components/documents/DocumentUploadButton';
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { toast as sonnerToast } from 'sonner';
import { FinalizeWithSignatures } from '@/components/documents/FinalizeWithSignatures';
import { embedSignaturesIntoHtml } from '@/lib/signatureEmbedding';
import { DocumentSigningControl } from '../DocumentSigningControl';
import { SealApplyCheckbox } from '../SealApplyCheckbox';
import { useAssociationSeal } from '../useAssociationSeal';
import { generatedFilePath } from '../docProvenance';
import { finishLegacyHtml } from '../legacyDocumentFinish';
import { legacySigningMeta } from '../legacySigningSpecs';
import { downloadDocumentById } from '@/lib/downloadDocumentById';

interface OfficerPhase2Props {
  task: any;
  phaseData: any;
  onPhaseComplete: () => void;
}

export default function OfficerPhase2({ task, phaseData, onPhaseComplete }: OfficerPhase2Props) {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<any[]>([]);
  const seal = useAssociationSeal(task.una_id);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<string | null>(null);
  const [previewDocument, setPreviewDocument] = useState<any>(null);
  const [emailWizardOpen, setEmailWizardOpen] = useState(false);
  const [selectedDocForEmail, setSelectedDocForEmail] = useState<string | null>(null);
  const [signatureWizardOpen, setSignatureWizardOpen] = useState(false);
  const [selectedDocForSignature, setSelectedDocForSignature] = useState<any>(null);
  const [unaData, setUnaData] = useState<any>(null);
  const [requestingOfficerSignature, setRequestingOfficerSignature] = useState(false);
  const [documentStatuses, setDocumentStatuses] = useState<Record<string, { status: string; label: string; color: string }>>({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const workflowData = task.workflow_data || {};

  const requiredDocuments = [
    { 
      key: 'letter_officer_appointment', 
      title: 'Officer Appointment Letter',
      requiresSignature: true
    },
    { 
      key: 'letter_officer_acceptance', 
      title: 'Officer Acceptance Letter',
      requiresSignature: true
    },
    { 
      key: 'certificate_officer_appointment', 
      title: 'Officer Appointment Certificate',
      requiresSignature: true
    },
  ];

  useEffect(() => {
    loadDocuments();
    loadUnaData();
    checkPhaseCompletion();
  }, [task.id]);

  async function checkPhaseCompletion() {
    // Check if phase is already completed
    const { data } = await supabase
      .from('task_phases')
      .select('status')
      .eq('task_id', task.id)
      .eq('phase_number', 2)
      .maybeSingle();
    
    if (data?.status === 'completed') {
      setIsCompleted(true);
    }
  }

  useEffect(() => {
    updateDocumentStatuses();
  }, [documents]);

  async function loadUnaData() {
    if (!task.una_id) return;
    
    try {
      const { data: una, error: unaError } = await supabase
        .from('unas')
        .select('*, una_members!una_members_una_id_fkey(*)')
        .eq('id', task.una_id)
        .single();

      if (unaError) throw unaError;
      setUnaData(una);
    } catch (error: any) {
      console.error('Error loading UNA data:', error);
    }
  }

  async function loadDocuments() {
    try {
      setLoading(true);
      
      const { data: phase2Docs, error } = await supabase
        .from('generated_documents')
        .select('*')
        .eq('task_id', task.id)
        .contains('merge_field_data', { phase: 'phase_2' })
        .order('generated_at', { ascending: true });

      if (error) throw error;
      setDocuments(phase2Docs || []);
    } catch (error: any) {
      console.error('Error loading documents:', error);
      toast({
        title: 'Error Loading Documents',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  async function updateDocumentStatuses() {
    const statuses: Record<string, { status: string; label: string; color: string }> = {};
    
    for (const docReq of requiredDocuments) {
      const doc = documents.find(d => d.merge_field_data?.template_key === docReq.key);
      
      if (!doc) {
        statuses[docReq.key] = { status: 'not_generated', label: 'Not Generated', color: 'secondary' };
        continue;
      }

      // Check for officer signature requests specifically for Acceptance Letter
      if (docReq.key === 'letter_officer_acceptance' && docReq.requiresSignature) {
        const { data: sigRequests } = await supabase
          .from('signature_requests')
          .select('status')
          .eq('document_id', doc.id)
          .eq('role_signing_as', 'Officer');

        const pendingOfficerSig = sigRequests?.find(sr => sr.status === 'pending');
        const completedOfficerSig = sigRequests?.find(sr => sr.status === 'completed');

        if (pendingOfficerSig) {
          statuses[docReq.key] = { status: 'pending_officer', label: 'Pending Officer Signature', color: 'secondary' };
          continue;
        }

        if (completedOfficerSig && doc.status === 'draft') {
          statuses[docReq.key] = { status: 'officer_signed', label: 'Officer Signed - Awaiting TS', color: 'default' };
          continue;
        }
      }

      if (docReq.requiresSignature) {
        if (doc.status === 'draft') {
          statuses[docReq.key] = { status: 'draft', label: 'Draft - Awaiting Signature', color: 'secondary' };
          continue;
        }
        if (doc.status === 'finalized') {
          statuses[docReq.key] = { status: 'finalized', label: 'Signed & Complete', color: 'default' };
          continue;
        }
      }

      statuses[docReq.key] = { status: 'generated', label: 'Generated', color: 'default' };
    }
    
    setDocumentStatuses(statuses);
  }

  async function handleRequestOfficerSignature(doc: any) {
    setRequestingOfficerSignature(true);
    try {
      const { error } = await supabase.functions.invoke('send-officer-signature-request', {
        body: {
          documentId: doc.id,
          taskId: task.id,
          unaId: task.una_id,
        }
      });
      
      if (error) throw error;
      
      toast({
        title: 'Success',
        description: `Signature request sent to ${workflowData.officer_email}`,
      });
      
      await loadDocuments();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setRequestingOfficerSignature(false);
    }
  }

  async function handleGenerateDocument(docReq: any) {
    try {
      setGenerating(docReq.key);

      const { data: unaData } = await supabase
        .from('unas')
        .select('*')
        .eq('id', task.una_id)
        .single();

      if (!unaData) throw new Error('UNA data not found');

      const html = finishLegacyHtml(
        generateOfficerPhase2DocumentHtml(docReq.key, workflowData, unaData),
        { entityName: unaData?.name, sealEnabled: seal.sealEnabled, sealSvg: seal.sealSvg },
      );

      const { data: pdfData, error: pdfError } = await supabase.functions.invoke('generate-pdf-server', {
        body: { html, author: unaData?.name || undefined },
      });

      if (pdfError) throw pdfError;
      if (!pdfData?.pdf) throw new Error('No PDF data returned');

      const pdfBlob = new Blob(
        [Uint8Array.from(atob(pdfData.pdf), c => c.charCodeAt(0))],
        { type: 'application/pdf' }
      );

      const filePath = generatedFilePath(unaData?.name, docReq.key, task.id);

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      const { data: { session: authSession } } = await supabase.auth.getSession();
      const createdBy = task.user_id || authSession?.user?.id || null;

      const { data: doc, error: insertError } = await supabase
        .from('generated_documents')
        .insert({
          una_id: task.una_id,
          task_id: task.id,
          document_name: docReq.title,
          document_type: 'workflow',
          file_url: publicUrl,
          file_path: filePath,
          file_size: pdfBlob.size,
          status: docReq.requiresSignature ? 'draft' : 'final',
          custom_content: html,
          created_by: createdBy,
          merge_field_data: { 
            phase: 'phase_2',
            template_key: docReq.key,
            ...legacySigningMeta(docReq.key, seal.sealEnabled),
            workflow_data: workflowData 
          },
        })
        .select()
        .single();

      if (insertError) throw insertError;

      sonnerToast.success('Document Generated', {
        description: `${docReq.title} created successfully`,
      });

      await loadDocuments();
    } catch (error: any) {
      console.error('Error generating document:', error);
      toast({
        title: 'Generation Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setGenerating(null);
    }
  }

  function handleCollectSignature(doc: any) {
    setSelectedDocForSignature(doc);
    setSignatureWizardOpen(true);
  }

  async function handleMarkPhaseComplete() {
    // Per established pattern: at least one document generated is sufficient
    if (documents.length === 0) {
      toast({
        title: 'Documents Required',
        description: 'Please generate at least one document before completing this phase.',
        variant: 'destructive',
      });
      return;
    }

    setIsCompleting(true);

    toast({
      title: 'Phase 2 Complete',
      description: 'Officer notification letters generated and signed. Proceeding to final phase.',
    });

    setIsCompleted(true);
    setIsCompleting(false);
    onPhaseComplete();
  }

  // Check if at least one document is generated (per established pattern)
  const allComplete = documents.length >= 1;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Phase 2: Generate Notification Documents</CardTitle>
          <CardDescription>
            Create appointment letters, acceptance letters, and certificates for the new officer
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Steps to Complete Phase 2:
            </h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-6">
              <li>Generate Officer Appointment Letter</li>
              <li>Generate Officer Acceptance Letter (for officer to sign)</li>
              <li>Generate Officer Appointment Certificate</li>
              <li>Distribute documents to officer and relevant parties</li>
              <li>Mark phase as complete</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Required Documents</CardTitle>
          <CardDescription>Generate all Phase 2 notification documents</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <SealApplyCheckbox
              available={seal.sealAvailable}
              checked={seal.applySeal}
              onChange={seal.setApplySeal}
            />
            {requiredDocuments.map((docReq) => {
              const statusInfo = documentStatuses[docReq.key] || { status: 'not_generated', label: 'Not Generated', color: 'secondary' };
              const { status, label, color } = statusInfo;
              const doc = documents.find(d => d.merge_field_data?.template_key === docReq.key);
              const isGenerating = generating === docReq.key;
              const isAcceptanceLetter = docReq.key === 'letter_officer_acceptance';

              return (
                <div key={docReq.key} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="font-medium">{docReq.title}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={color as any}>{label}</Badge>
                    
                    {!doc ? (
                      <Button
                        onClick={() => handleGenerateDocument(docReq)}
                        disabled={isGenerating}
                        size="sm"
                      >
                        {isGenerating ? 'Generating...' : 'Generate'}
                      </Button>
                    ) : (
                      <>
                        {/* Acceptance Letter: Show "Request Officer Signature" button */}
                        {isAcceptanceLetter && doc.status === 'draft' && status === 'draft' && (
                          <Button 
                            onClick={() => handleRequestOfficerSignature(doc)}
                            disabled={requestingOfficerSignature}
                            size="sm"
                          >
                            {requestingOfficerSignature ? 'Sending...' : 'Request Officer Signature'}
                          </Button>
                        )}

                        {/* Show Sign & Finalize for all draft docs or when officer has signed */}
                        {doc.status === 'draft' && docReq.requiresSignature && status !== 'pending_officer' && (
                          // For Acceptance Letter: Only show TS button after officer has signed
                          (!isAcceptanceLetter || status === 'officer_signed') && (
                            <Button onClick={() => handleCollectSignature(doc)} size="sm">
                              Sign & Finalize (TS)
                            </Button>
                          )
                        )}
                        
                        {doc && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPreviewDocument(doc)}
                            >
                              <Eye className="h-3 w-3" />
                              Preview
                            </Button>
                          
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                try {
                                  await downloadDocumentById(doc.id, { underscoreFilename: true });
                                } catch (error) {
                                  console.error('Download error:', error);
                                }
                              }}
                            >
                              <Download className="h-3 w-3" />
                              Download
                            </Button>

                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedDocForEmail(doc.id);
                                setEmailWizardOpen(true);
                              }}
                            >
                              <Mail className="h-3 w-3" />
                              Email
                            </Button>
                            
                            <DocumentUploadButton
                              documentId={doc.id}
                              documentName={doc.document_name}
                              onUploadComplete={() => loadDocuments()}
                              fileUrl={doc.file_url}
                              hasUploadedFile={doc.file_path?.includes('-uploaded.pdf')}
                            />
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Complete Phase - Show completion state or button */}
          {isCompleted ? (
            <Alert className="mt-6 border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              <AlertDescription>
                <div className="ml-2">
                  <p className="font-medium text-green-800 dark:text-green-200">Phase 2 Complete</p>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Officer notification documents have been generated and signed. Proceed to Phase 3 for distribution and filing.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          ) : (
            <div className={`mt-6 p-4 rounded-lg ${allComplete ? 'bg-green-50 dark:bg-green-950' : 'bg-muted/50'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`font-medium ${allComplete ? 'text-green-900 dark:text-green-100' : 'text-muted-foreground'}`}>
                    {allComplete ? 'Phase 2 Requirements Met' : 'Complete Phase 2'}
                  </p>
                  <p className={`text-sm ${allComplete ? 'text-green-700 dark:text-green-300' : 'text-muted-foreground'}`}>
                    {allComplete 
                      ? 'At least one notification document has been generated' 
                      : 'Generate at least one notification document to continue'}
                  </p>
                </div>
                <Button 
                  onClick={handleMarkPhaseComplete}
                  disabled={!allComplete || isCompleting}
                >
                  {isCompleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Completing...
                    </>
                  ) : (
                    'Complete Phase 2'
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          open={!!previewDocument}
          onClose={() => setPreviewDocument(null)}
        />
      )}

      {signatureWizardOpen && selectedDocForSignature && (
        <FinalizeWithSignatures
          documentTitle={`Finalize ${selectedDocForSignature?.document_name || 'Document'}`}
          treasurySecretaryName={unaData?.treasury_secretary_name || 'Secretary Treasurer'}
          primaryOfficerRoleLabel={unaData?.primary_officer_role || 'Secretary Treasurer'}
          requiredSigners={['treasury_secretary']}
          onFinalize={async (signatures) => {
            try {
              // Fetch FRESH custom_content from database to preserve any signatures already embedded
              const { data: freshDocData, error: fetchError } = await supabase
                .from('generated_documents')
                .select('custom_content')
                .eq('id', selectedDocForSignature.id)
                .single();
              
              if (fetchError) throw fetchError;
              if (!freshDocData?.custom_content) {
                throw new Error('Document HTML not found');
              }
              
              const signatureDataArray = [{
                role: 'treasury_secretary',
                signer_id: 'treasury_secretary',
                signature_data: signatures.treasury_secretary_signature,
                signed_at: signatures.treasury_secretary_signature_date,
              }];

              const updatedHtml = await embedSignaturesIntoHtml(
                freshDocData.custom_content,
                signatureDataArray,
                new Date().toISOString()
              );

              const { data: pdfData, error: pdfError } = await supabase.functions.invoke('generate-pdf-server', {
                body: { html: updatedHtml, author: unaData?.name || undefined },
              });

              if (pdfError) throw pdfError;
              if (!pdfData?.pdf) throw new Error('No PDF data returned');

              const pdfBlob = new Blob(
                [Uint8Array.from(atob(pdfData.pdf), c => c.charCodeAt(0))],
                { type: 'application/pdf' }
              );

              const filePath = generatedFilePath(unaData?.name, `${selectedDocForSignature.merge_field_data?.template_key}_signed`, selectedDocForSignature.id);

              const { error: uploadError } = await supabase.storage
                .from('documents')
                .upload(filePath, pdfBlob, {
                  contentType: 'application/pdf',
                  upsert: true,
                });

              if (uploadError) throw uploadError;

              const { data: { publicUrl } } = supabase.storage
                .from('documents')
                .getPublicUrl(filePath);

              const { error: updateError } = await supabase
                .from('generated_documents')
                .update({
                  status: 'finalized',
                  finalized_at: new Date().toISOString(),
                  finalized_by: task.user_id,
                  file_url: publicUrl,
                  file_path: filePath,
                  file_size: pdfBlob.size,
                  signed_file_url: publicUrl,
                  custom_content: updatedHtml,
                  signature_history: {
                    finalized_signatures: {
                      treasury_secretary_signature: signatures.treasury_secretary_signature,
                      treasury_secretary_date: signatures.treasury_secretary_signature_date,
                      finalized_at: new Date().toISOString(),
                    }
                  }
                })
                .eq('id', selectedDocForSignature.id);

              if (updateError) throw updateError;

              sonnerToast.success('Document Finalized', {
                description: `${unaData?.primary_officer_role || 'Secretary Treasurer'} signature applied successfully`,
              });

              setSignatureWizardOpen(false);
              setSelectedDocForSignature(null);
              await loadDocuments();
            } catch (error: any) {
              console.error('Error finalizing document:', error);
              toast({
                title: 'Finalization Failed',
                description: error.message,
                variant: 'destructive',
              });
            }
          }}
          onCancel={() => {
            setSignatureWizardOpen(false);
            setSelectedDocForSignature(null);
          }}
        />
      )}
    </div>
  );
}

// Helper function to generate Phase 2 officer documents with professional styling
function generateOfficerPhase2DocumentHtml(docKey: string, workflowData: any, unaData: any): string {
  const professionalStyles = `
    <style>
      @page { margin: 1in; }
      body { 
        font-family: Cambria, 'Times New Roman', serif; 
        font-size: 11pt;
        line-height: 1.5;
        color: #000;
        margin: 0;
        padding: 0;
      }
      .letterhead { 
        text-align: center; 
        font-weight: bold;
        font-size: 14pt;
        margin-bottom: 0.5in;
        letter-spacing: 1px;
      }
      .date { 
        margin-bottom: 0.3in; 
        font-style: italic;
      }
      .address-block {
        margin-bottom: 0.3in;
        line-height: 1.3;
      }
      .re-line {
        font-weight: bold;
        margin: 0.2in 0;
      }
      .signature-block {
        margin-top: 0.6in;
        page-break-inside: avoid;
      }
      .signature-line {
        display: inline-block;
        width: 3in;
        border-bottom: 1px solid #000;
        margin-bottom: 0.05in;
      }
      .section-title {
        font-weight: bold;
        margin-top: 0.25in;
        margin-bottom: 0.1in;
      }
      .certificate-border {
        border: 3px double #000;
        padding: 0.5in;
        text-align: center;
      }
      .certificate-inner-border {
        border: 1px solid #000;
        padding: 0.75in 0.5in;
      }
      .certificate-title {
        font-size: 18pt;
        font-weight: bold;
        letter-spacing: 2px;
        margin-bottom: 0.3in;
      }
      .certificate-subtitle {
        font-size: 14pt;
        font-weight: bold;
        margin-bottom: 0.5in;
      }
      .certificate-name {
        font-size: 16pt;
        font-weight: bold;
        margin: 0.4in 0;
        text-decoration: underline;
      }
      .certificate-position {
        font-size: 14pt;
        font-weight: bold;
        font-style: italic;
        margin: 0.3in 0;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin: 0.2in 0;
      }
      td {
        padding: 0.1in 0.15in;
        vertical-align: top;
      }
      ul {
        margin: 0.1in 0;
        padding-left: 0.3in;
      }
      li {
        margin-bottom: 0.05in;
      }
    </style>
  `;

  // Helper function to format dates consistently
  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const officerName = workflowData.officer_full_name || '[Officer Name]';
  const officerPosition = workflowData.officer_position || '[Officer Position]';
  const effectiveDate = formatDate(workflowData.effective_date);
  const approvalDate = formatDate(workflowData.board_approval_date);
  const acceptanceDeadline = formatDate(workflowData.acceptance_deadline);
  const unaName = unaData?.name || '[UNA Name]';

  if (docKey === 'letter_officer_appointment') {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        ${professionalStyles}
      </head>
      <body>
        <div class="letterhead">${unaName}</div>
        
        <div class="date">${today}</div>
        
        <div class="address-block">
          ${officerName}<br>
          ${workflowData.officer_address || '[Street Address]'}<br>
          ${workflowData.officer_city || '[City]'}, ${workflowData.officer_state || '[State]'} ${workflowData.officer_zip || '[ZIP]'}${workflowData.officer_country && workflowData.officer_country !== 'US' ? `<br>${workflowData.officer_country}` : ''}
        </div>
        
        <div class="re-line">Re: Appointment as ${officerPosition}</div>
        
        <p>Dear ${officerName.split(' ')[0] || officerName},</p>
        
        <p>On behalf of the Board of ${unaName}, I am pleased to officially notify you of your appointment to the position of <strong>${officerPosition}</strong>, effective ${effectiveDate}.</p>
        
        <p>Your appointment was unanimously approved by the Board on ${approvalDate}. The Board is confident that your expertise, dedication, and leadership will contribute significantly to the success and growth of ${unaName}.</p>
        
        <div class="section-title">Key Responsibilities:</div>
        <ul>
          <li>Execute duties as outlined in the ${unaName} Operating Agreement and applicable state law</li>
          <li>Act in the best interests of ${unaName} and its members at all times</li>
          <li>Maintain accurate records and ensure compliance with reporting requirements</li>
          <li>Collaborate with other officers and board members to advance organizational goals</li>
          <li>${workflowData.additional_responsibilities || 'Additional responsibilities as assigned by the Board'}</li>
        </ul>
        
        <div class="section-title">Term of Office:</div>
        <p>Your appointment is effective ${effectiveDate} and shall continue until your successor is appointed, you resign, or the Board removes you in accordance with the Operating Agreement.</p>
        
        <div class="section-title">Authority and Resources:</div>
        <p>As ${officerPosition}, you are granted the authority necessary to fulfill your duties. ${unaName} will provide reasonable support and resources to enable you to perform your responsibilities effectively.</p>
        
        <div class="section-title">Next Steps:</div>
        <ul>
          <li>Please review the enclosed Officer Duties & Responsibilities Addendum</li>
          <li>Review and return the Officer Acceptance Letter</li>
          <li>Contact ${unaData?.treasury_secretary_name || `the ${unaData?.primary_officer_role || 'Secretary Treasurer'}`} to schedule an orientation meeting</li>
        </ul>
        
        <p>Congratulations on your appointment. We look forward to your contributions to ${unaName} and are excited to work alongside you.</p>
        
        <div class="signature-block">
          <p>Sincerely,</p>
          <p style="margin-top: 0.5in;">
            <div data-signer-id="treasury_secretary" data-signature-role="${unaData?.primary_officer_role || 'Secretary Treasurer'}" style="display: inline-block; min-height: 0.8in; width: 3.5in; border-bottom: 1px solid #000; margin-bottom: 0.1in;">
              <em style="color: #666; font-size: 9pt;">[${unaData?.primary_officer_role || 'Secretary Treasurer'} Signature]</em>
            </div><br>
            ${unaData?.treasury_secretary_name || unaData?.primary_officer_role || 'Secretary Treasurer'}<br>
            ${unaData?.primary_officer_role || 'Secretary Treasurer'}, ${unaName}<br>
            Date: ${today}
          </p>
        </div>
        
        <p style="margin-top: 0.4in; font-style: italic;">
          <strong>Enclosures:</strong><br>
          • Officer Duties & Responsibilities Addendum<br>
          • Officer Acceptance Letter<br>
          • Copy of Board Resolution
        </p>
      </body>
      </html>
    `;
  }

  if (docKey === 'letter_officer_acceptance') {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        ${professionalStyles}
      </head>
      <body>
        <div class="letterhead">OFFICER ACCEPTANCE LETTER</div>
        
        <div class="date">${today}</div>
        
        <p><strong>To:</strong> Board of ${unaName}</p>
        <p><strong>From:</strong> ${officerName}, ${officerPosition}-Designate</p>
        <p><strong>Re:</strong> Acceptance of Officer Appointment</p>
        
        <hr style="margin: 0.3in 0; border: none; border-top: 1px solid #000;">
        
        <p>I, ${officerName}, hereby formally accept the appointment to serve as <strong>${officerPosition}</strong> of ${unaName}, effective ${effectiveDate}.</p>
        
        <div class="section-title">Acknowledgment of Documents Received:</div>
        <p>I acknowledge receipt and thorough review of the following documents:</p>
        <ul>
          <li>Officer Appointment Letter dated ${today}</li>
          <li>Officer Duties & Responsibilities Addendum</li>
          <li>Copy of Board Resolution approving my appointment</li>
          <li>${unaName} Operating Agreement</li>
        </ul>
        
        <div class="section-title">Responsibilities Acknowledged:</div>
        <p>I understand and acknowledge that as ${officerPosition}, I will be responsible for:</p>
        <ul>
          <li>Fulfilling all duties specified in the Operating Agreement and applicable law</li>
          <li>Acting in good faith and in the best interests of ${unaName}</li>
          <li>Maintaining confidentiality of sensitive organizational information</li>
          <li>Complying with all applicable federal, state, and local laws</li>
          <li>Participating actively in Board meetings and organizational initiatives</li>
        </ul>
        
        <div class="section-title">Commitment Statement:</div>
        <p>I commit to executing the duties of ${officerPosition} with diligence, integrity, and professionalism. I understand that this position requires dedication and that I am expected to act in accordance with the highest ethical standards.</p>
        
        <p>I further acknowledge that I may be removed from this position in accordance with the procedures outlined in the Operating Agreement should I fail to fulfill my responsibilities or act contrary to the interests of ${unaName}.</p>
        
        <div class="signature-block">
          <table style="margin-top: 0.6in;">
            <tr>
              <td style="width: 50%;">
                <strong>Officer Signature:</strong><br>
                <div data-signer-id="officer" data-signature-role="Officer" style="width: 3in; min-height: 0.8in; border-bottom: 1px solid #000; display: flex; align-items: center; justify-content: center; margin-bottom: 0.1in;">
                  <em style="color: #666; font-size: 9pt;">[Officer Signature]</em>
                </div>
                <span style="font-size: 9pt;">${officerName}, ${officerPosition}-Designate</span>
              </td>
              <td style="width: 50%;">
                <strong>Date:</strong><br>
                <span style="font-size: 9pt;">${today}</span>
              </td>
            </tr>
          </table>
          
          <p style="margin-top: 0.3in;">
            <strong>Print Name:</strong> ${officerName}
          </p>
          
          <hr style="margin: 0.5in 0; border: none; border-top: 1px solid #ccc;">
          
          <p><strong>Acknowledged and Accepted by ${unaName}:</strong></p>
          <table style="margin-top: 0.3in;">
            <tr>
              <td style="width: 60%;">
                <strong>${unaData?.primary_officer_role || 'Secretary Treasurer'} Signature:</strong><br>
                <div data-signer-id="primary_officer" data-signature-role="${unaData?.primary_officer_role || 'Secretary Treasurer'}" style="width: 3in; min-height: 0.8in; border-bottom: 1px solid #000; display: flex; align-items: center; justify-content: center; margin-bottom: 0.1in;">
                  <em style="color: #666; font-size: 9pt;">[${unaData?.primary_officer_role || 'Secretary Treasurer'} Signature]</em>
                </div>
                <span style="font-size: 9pt;">${unaData?.treasury_secretary_name || unaData?.primary_officer_role || 'Secretary Treasurer'}</span>
              </td>
              <td style="width: 40%;">
                <strong>Date:</strong><br>
                <span style="font-size: 9pt;">${today}</span>
              </td>
            </tr>
          </table>
        </div>
        
        <p style="margin-top: 0.5in; font-style: italic; font-size: 9pt; border-top: 1px solid #ccc; padding-top: 0.2in;">
          <strong>Instructions:</strong> Please sign, date, and return this letter to the ${unaData?.primary_officer_role || 'Secretary Treasurer'} of ${unaName} by ${acceptanceDeadline}. You may scan and email it or deliver a physical copy.
        </p>
      </body>
      </html>
    `;
  }

  if (docKey === 'certificate_officer_appointment') {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        ${professionalStyles}
      </head>
      <body>
        <div class="certificate-border">
          <div class="certificate-inner-border">
            <div class="certificate-title">CERTIFICATE OF OFFICER APPOINTMENT</div>
            
            <div class="certificate-subtitle">${unaName}</div>
            
            <p style="margin: 0.5in 0 0.3in 0; font-size: 11pt;">This is to certify that</p>
            
            <div class="certificate-name">${officerName}</div>
            
            <p style="margin: 0.3in 0; font-size: 11pt;">has been duly appointed to the office of</p>
            
            <div class="certificate-position">${officerPosition}</div>
            
            <p style="margin: 0.3in 0; font-size: 11pt;">of ${unaName}</p>
            
            <p style="margin: 0.4in 0 0.2in 0; font-size: 11pt;">by resolution of the Board adopted on</p>
            
            <p style="font-weight: bold; font-size: 12pt; margin-bottom: 0.3in;">${approvalDate}</p>
            
            <p style="margin: 0.2in 0 0.5in 0; font-size: 11pt;">with an effective date of</p>
            
            <p style="font-weight: bold; font-size: 12pt; margin-bottom: 0.6in;">${effectiveDate}</p>
            
            <div style="margin-top: 0.6in; position: relative;">
              <table style="width: 100%; margin-top: 0.4in;">
                <tr>
                  <td style="width: 50%; text-align: center; padding: 0;">
                    <div style="margin: 0 auto; width: 3in;">
                       <div data-signer-id="treasury_secretary" data-signature-role="${unaData?.primary_officer_role || 'Secretary Treasurer'}" style="min-height: 1in; display: flex; flex-direction: column; align-items: center; justify-content: center; border-bottom: 1px solid #000;">
                        <em style="color: #666; font-size: 9pt;">[${unaData?.primary_officer_role || 'Secretary Treasurer'} Signature]</em>
                      </div>
                      <div style="font-size: 10pt; margin-top: 0.1in;">
                        <strong>${unaData?.primary_officer_role || 'Secretary Treasurer'}</strong><br>
                        ${unaName}
                      </div>
                    </div>
                  </td>
                  <td style="width: 50%; text-align: center; padding: 0;">
                    <div style="margin: 0 auto; width: 2.5in;">
                      <div style="font-size: 11pt; padding-top: 0.3in;">${today}</div>
                      <div style="font-size: 10pt; margin-top: 0.1in;">
                        <strong>Date</strong>
                      </div>
                    </div>
                  </td>
                </tr>
              </table>
            </div>
            
            <div style="margin-top: 0.6in; border-top: 2px solid #000; padding-top: 0.2in;">
              <p style="font-size: 9pt; font-style: italic; margin: 0;">
                Official ${unaName} Seal
              </p>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  return '';
}
